import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import BarLoaderComponent from 'webapp/components/bar-loader';
import FormRangeListComponent from 'webapp/components/form/range/list';
import type { Item } from 'webapp/components/form/range/list-base';
import type { Args } from 'webapp/components/popup/inner/context';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';

import type DateRange from 'webapp/models-custom/date-range';

export default class FormRangePopupComponent extends PopupInnerContextComponent<PopupData.Form.Range> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('analytics') declare analyticsService: Service.Analytics;

	selectedDateRange: DateRange | null = null;

	@tracked isLoading = true;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.DATE_RANGE_DROPDOWN_OPENED, {
			origin: this.popupData.trackingOrigin,
		});
		this.args.model.on(PopupEvent.CLOSING, this, this.onPopupClosing);

		this.metricLimitations.updateLimitations();
		void this.metricLimitations.limitationsLoaded?.then(() => {
			this.isLoading = false;
			this.adjustPosition();
		});
	}

	get metricLimitations() {
		return this.popupData.metricLimitations;
	}

	get dateRange() {
		return this.metricLimitations.dateRange!;
	}

	get exceptionIds() {
		return this.popupData.exceptionIds;
	}

	get showAllTime() {
		return this.popupData.showAllTime;
	}

	get hideUnsupported() {
		return !this.currentUserService.isLoggedIn;
	}

	adjustPosition() {
		setTimeout(() => {
			const popupElement = document.querySelector('.popup-cdr')!;
			if (popupElement) {
				const popupRect = popupElement.getBoundingClientRect();
				const popupBottomOverflow = popupRect.top + popupRect.height - window.innerHeight;

				if (popupBottomOverflow > 0) {
					this.args.model.reposition();
				}
			}
		}, 10);
	}

	onPopupClosing() {
		if (
			this.dateRange &&
			this.selectedDateRange &&
			!this.dateRange.isRangeEqual(this.selectedDateRange.formula)
		) {
			this.popupData.onChange(this.selectedDateRange);
		}
	}

	@action
	selectItem(item: Item, skipClose = false) {
		this.selectedDateRange = item as DateRange;
		if (!skipClose) {
			this.close();
		}
	}
	<template>
		{{#if this.isLoading}}
			<div class="loader-ct">
				<BarLoaderComponent />
			</div>
		{{else if this.metricLimitations}}
			<FormRangeListComponent
				@theme={{@model.theme}}
				@initialDateRange={{this.dateRange}}
				@metricLimitations={{this.metricLimitations}}
				@exceptionIds={{this.exceptionIds}}
				@showAllTime={{this.showAllTime}}
				@hideUnsupported={{this.hideUnsupported}}
				@onSelectItem={{this.selectItem}}
				@onContentChange={{this.reposition}}
				@customPeriodPickerOptions={{this.popupData.customPeriodPickerOptions}}
			/>
		{{/if}}
	</template>
}
